/**
 * A room's sessions, as the sheet over the page shows them: a series on one timeline or a month, its
 * replays and "Leave this series"; a recorded talk or a one-time live as its own row.
 */
import { type ReactNode, useState } from "react";
import { labelParts } from "../../../shared/live-time";
import type { WatchSession } from "../../../shared/types";
import { startedWords } from "../series";
import { usePage } from "./context";
import { IcBack, IcCal, IcCalSub, IcCheck, IcChev } from "./icons";
import { clock, monthOf, monthOfRow, type TimelineRow, talkLength, timelineOf, todayIndex } from "./model";
import { continueWords, seatHref, useCalendarMenu } from "./Pinned";
import { kindWord } from "./talk";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** "Tue, Nov 3", on their own clock. */
function dayWords(iso: string, long = false): string {
  const p = labelParts(iso, undefined, long ? "long" : "short");
  return p ? `${long ? p.weekday : p.weekday.slice(0, 3)}, ${p.month.slice(0, 3)} ${p.day}` : "";
}
const timeWords = (iso: string) => labelParts(iso, undefined, "short")?.time.replace(" ", " ") ?? "";

function SeatLine({ room, row }: { room: WatchSession; row: TimelineRow }): ReactNode {
  const { token, nowMs, inCalendar, playTalk } = usePage();
  const calendar = useCalendarMenu();
  if (row.kind === "past")
    return (
      <>
        <span className="tl-s">
          {row.dot === "came" ? <IcCheck s={13} /> : null}
          {row.stat}
        </span>
        {row.go ? (
          <span className="tl-a">
            <button type="button" className="tlink" onClick={() => playTalk(room.webinar_id, { sessionId: row.session.id, start: true })}>
              {row.go}
            </button>
          </span>
        ) : row.session.replay === "preparing" ? (
          <span className="tl-s">The replay is being prepared</span>
        ) : null}
      </>
    );
  if (row.kind === "skip") return <span className="tl-s">{row.stat}</span>;
  if (row.dot === "live")
    return (
      <>
        <span className="tl-s live">
          <i className="ldot" />
          Live now{room.live?.wentLiveAt ? ` · ${startedWords(room.live.wentLiveAt, nowMs).toLowerCase()}` : ""}
        </span>
        <span className="tl-a">
          <a className="btn btn-p btn-sm" href={seatHref(room, token)}>
            Join the room
          </a>
        </span>
      </>
    );
  if (!row.stat) return null;
  const added = inCalendar(room.webinar_id);
  return (
    <>
      <span className="tl-s ok">
        <IcCheck s={13} />
        {row.stat}
      </span>
      {room.calendar ? (
        <span className="tl-a">
          {added ? (
            <span className="incal">
              <IcCheck s={14} />
              In your calendar
            </span>
          ) : (
            <button type="button" className="btn btn-p btn-sm" data-menu-anchor onClick={(e) => calendar(e.currentTarget, room)}>
              <IcCal s={14} />
              Add to calendar
            </button>
          )}
        </span>
      ) : null}
    </>
  );
}

function whenOf(row: TimelineRow, nowMs: number, long = false): string {
  const iso = row.session.startsAt;
  const days = Math.round((Date.parse(iso) - nowMs) / 86_400_000);
  const pre = row.dot === "live" ? "Now · " : row.kind === "next" && days <= 1 && days >= 0 && Date.parse(iso) > nowMs ? (new Date(iso).getDate() === new Date(nowMs).getDate() ? "Today · " : "Tomorrow · ") : "";
  return `${pre}${dayWords(iso, long)}${row.kind === "past" || row.kind === "skip" ? "" : ` · ${timeWords(iso)}`}`;
}
const rowTitle = (row: TimelineRow) => `Session ${row.session.seq}${row.session.topic ? ` · ${row.session.topic}` : ""}`;

function Timeline({ room, rows }: { room: WatchSession; rows: TimelineRow[] }): ReactNode {
  const { nowMs } = usePage();
  const at = todayIndex(rows);
  const items: ReactNode[] = [];
  rows.forEach((r, i) => {
    if (i === at)
      items.push(
        <li className="tl-today" key="today">
          <span className="tl-dot" />
          Today · {dayWords(new Date(nowMs).toISOString())}
        </li>,
      );
    items.push(
      <li className={`tl-i k-${r.kind}`} key={r.key}>
        <span className={`tl-dot d-${r.dot === "nolive" ? "missed" : r.dot}`} />
        <div className="tl-x">
          <span className="tl-d">{whenOf(r, nowMs)}</span>
          <b className="tl-t">{rowTitle(r)}</b>
          <SeatLine room={room} row={r} />
        </div>
      </li>,
    );
  });
  if (at >= rows.length)
    items.push(
      <li className="tl-today" key="today">
        <span className="tl-dot" />
        Today · {dayWords(new Date(nowMs).toISOString())}
      </li>,
    );
  return <ol className="tl">{items}</ol>;
}

function Month({ room, rows }: { room: WatchSession; rows: TimelineRow[] }): ReactNode {
  const { nowMs, inCalendar } = usePage();
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const months = rows.map((r) => monthOfRow(r, tz)).filter((m): m is { year: number; month: number } => Boolean(m));
  const key = (m: { year: number; month: number }) => m.year * 12 + m.month;
  const span = [...new Set(months.map(key))].toSorted((a, b) => a - b);
  const now = new Date(nowMs);
  const nowKey = now.getFullYear() * 12 + now.getMonth();
  const next = rows.find((r) => r.kind === "next");
  const nextMonth = next ? monthOfRow(next, tz) : null;
  const [shown, setShown] = useState(nextMonth ? key(nextMonth) : (span.at(-1) ?? nowKey));
  const [picked, setPicked] = useState<string | null>(null);
  const year = Math.floor(shown / 12);
  const month = shown % 12;
  const { lead, cells } = monthOf(rows, year, month, tz);
  const todayDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const defaultSel = cells.find((c) => c.row === next)?.date ?? cells.find((c) => c.row)?.date ?? null;
  const sel = picked && cells.some((c) => c.date === picked) ? picked : defaultSel;
  const selCell = cells.find((c) => c.date === sel);
  const i = span.indexOf(shown);
  const prev = i > 0 ? span[i - 1] : null;
  const after = i >= 0 && i < span.length - 1 ? span[i + 1] : null;
  const state = (r: TimelineRow) => (r.kind === "past" ? (r.dot === "nolive" ? "missed" : r.dot) : r.dot);
  return (
    <div className="cal">
      <div className="cal-h">
        <button type="button" className="cal-nav" disabled={prev === null} aria-label={prev !== null ? MONTHS[prev % 12] : "Earlier"} onClick={() => prev !== null && setShown(prev)}>
          <IcBack s={18} />
        </button>
        <b>{MONTHS[month]}</b>
        <button type="button" className="cal-nav" disabled={after === null} aria-label={after !== null ? MONTHS[after % 12] : "Later"} onClick={() => after !== null && setShown(after)}>
          <IcChev s={18} />
        </button>
      </div>
      <div className="cal-g">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, k) => (
          <span key={`${d}${k}`} className="cal-wd">
            {d}
          </span>
        ))}
        {Array.from({ length: lead }, (_, k) => (
          <span key={`out${k}`} className="cday out" aria-hidden="true" />
        ))}
        {cells.map((c) => {
          const cls = ["cday", c.row ? `s-${state(c.row)}` : "", c.date === todayDate ? "today" : "", sel === c.date ? "sel" : ""].filter(Boolean).join(" ");
          const badge = c.row && c.row.kind !== "past" && c.row.kind !== "skip" && inCalendar(room.webinar_id);
          return (
            <button
              key={c.date}
              type="button"
              className={cls}
              aria-pressed={sel === c.date}
              aria-label={`${dayWords(`${c.date}T12:00:00Z`, true)}${c.row ? `, Session ${c.row.session.seq}` : ""}`}
              onClick={() => setPicked(c.date)}
            >
              {c.day}
              {badge ? (
                <i className="cin">
                  <IcCheck s={10} />
                </i>
              ) : null}
            </button>
          );
        })}
      </div>
      <div className="cal-key">
        <span>
          <i className="k k-saved" />
          Seat saved
        </span>
        <span>
          <i className="k k-past" />
          Past
        </span>
      </div>
      {selCell?.row ? (
        <div className="cal-det">
          <span className="tl-d">{whenOf(selCell.row, nowMs, true)}</span>
          <b className="tl-t">{rowTitle(selCell.row)}</b>
          <SeatLine room={room} row={selCell.row} />
        </div>
      ) : sel ? (
        <div className="cal-det empty">
          <span className="tl-d">{dayWords(`${sel}T12:00:00Z`, true)}</span>
          <p>Nothing that day.</p>
        </div>
      ) : null}
    </div>
  );
}

export function SeriesBlock({ room }: { room: WatchSession }): ReactNode {
  const { nowMs, inCalendar, markCalendar, toast, leaveSeries } = usePage();
  const series = room.live?.series;
  const [view, setView] = useState<"list" | "month">("list");
  const [confirm, setConfirm] = useState(false);
  if (!series) return null;
  const rows = timelineOf(room, nowMs);
  const sub = room.calendar?.subscribe;
  return (
    <div className="sp-series">
      <div className="sp-head">
        <b>{room.title}</b>
        <div className="sp-head2">
          <small>{series.long}</small>
          <div className="vtog" role="group" aria-label="Calendar view">
            <button type="button" className={view === "list" ? "on" : ""} aria-pressed={view === "list"} onClick={() => setView("list")}>
              List
            </button>
            <button type="button" className={view === "month" ? "on" : ""} aria-pressed={view === "month"} onClick={() => setView("month")}>
              Month
            </button>
          </div>
        </div>
      </div>
      {series.left ? (
        <div className="left-note">
          <p>You left this series.</p>
          <a className="btn btn-p btn-sm" href={`/w/${encodeURIComponent(room.slug)}`}>
            Join again
          </a>
        </div>
      ) : null}
      {view === "month" ? <Month room={room} rows={rows} /> : <Timeline room={room} rows={rows} />}
      {sub && !series.left && !series.ended ? (
        <div className="tl-foot">
          {inCalendar(room.webinar_id) ? (
            <span className="incal">
              <IcCheck s={14} />
              In your calendar
            </span>
          ) : (
            <a
              className="tlink"
              href={sub}
              onClick={() => {
                markCalendar(room.webinar_id);
                toast("Subscribed to the calendar.");
              }}
            >
              <IcCalSub s={15} />
              Subscribe to the calendar
            </a>
          )}
        </div>
      ) : null}
      {series.left || series.ended ? null : (
        <div className="leave">
          {confirm ? (
            <div className="confirm">
              <p>Leave {room.title}? Your replays and handouts stay here.</p>
              <div className="sh-acts">
                <button
                  type="button"
                  className="btn btn-p"
                  onClick={() => {
                    setConfirm(false);
                    toast("You’re staying in.");
                  }}
                >
                  Stay in
                </button>
                <button
                  type="button"
                  className="btn btn-g"
                  onClick={() => {
                    setConfirm(false);
                    leaveSeries(room.webinar_id).catch(() => toast("That did not go through. Try again."));
                  }}
                >
                  Leave
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="btn btn-q" onClick={() => setConfirm(true)}>
              Leave this series
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** A recorded talk, or a one-time live, as its own row. */
export function RoomRow({ room }: { room: WatchSession }): ReactNode {
  const { playTalk, token, nowMs, inCalendar, guest } = usePage();
  const calendar = useCalendarMenu();
  const live = room.live;
  const pct = room.duration_sec > 0 ? Math.min(100, (room.watched_sec / room.duration_sec) * 100) : 0;
  let sub = "";
  let action: ReactNode = null;
  if (!live) {
    sub = `${kindWord(guest.rooms.find((r) => r.webinarId === room.webinar_id)?.kind)} · ${talkLength(room.duration_sec)}${room.watched_sec > 0 ? ` · you are at ${clock(room.watched_sec)}` : ""}`;
    action = (
      <button type="button" className="tlink" onClick={() => playTalk(room.webinar_id, { start: true })}>
        {continueWords(room)}
      </button>
    );
  } else if (live.state === "live") {
    sub = "Live now";
    action = (
      <a className="btn btn-p btn-sm" href={seatHref(room, token)}>
        Join the room
      </a>
    );
  } else if (live.state === "scheduled") {
    sub = `${dayWords(live.startsAt)} · ${timeWords(live.startsAt)}`;
    action =
      nowMs >= Date.parse(live.opensAt) ? (
        <a className="btn btn-p btn-sm" href={seatHref(room, token)}>
          Join the room
        </a>
      ) : inCalendar(room.webinar_id) ? (
        <span className="incal">
          <IcCheck s={14} />
          In your calendar
        </span>
      ) : room.calendar ? (
        <button type="button" className="btn btn-p btn-sm" data-menu-anchor onClick={(e) => calendar(e.currentTarget, room)}>
          <IcCal s={14} />
          Add to calendar
        </button>
      ) : null;
  } else {
    sub = live.replay === "ready" ? "The replay is here" : live.replay === "preparing" ? "The replay is being prepared" : "It has ended";
    action =
      live.replay === "ready" ? (
        <button type="button" className="tlink" onClick={() => playTalk(room.webinar_id, { start: true })}>
          {room.watched_sec > 0 ? continueWords(room) : "Watch the replay"}
        </button>
      ) : null;
  }
  return (
    <li className="srow">
      <span className="st">
        {room.poster_url ? <img src={room.poster_url} alt="" /> : null}
        {pct > 0 ? <i style={{ width: `${pct}%` }} /> : null}
      </span>
      <span className="sx">
        <b>{room.title}</b>
        <small>{sub}</small>
        {action}
      </span>
    </li>
  );
}
